/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component, FormEvent, ReactNode } from 'react';
import { AppState } from '../../app.state';
import { Alert, message } from 'antd';
import moment, { Moment } from 'moment';
import { withRouter, RouteComponentProps } from 'react-router';
import FormTitle from '../../_shared/form-title';
import SharedStyles from '../../_shared/styles';
import { Button } from '../../_shared/button';
import Link from '../../_shared/link';
import styles from './edit-equipment.styles';
import {
  getEquipment,
  createOrUpdateEquipment,
} from '../../_shared/services/manage-equipment.service';
import { _Equipment, Equipment } from '../../_shared/interfaces/equipment';
import { Location } from '../../_shared/interfaces/location';

import {
  findModel,
  userHasRole,
  isDentalOrgUser,
  isServiceOrgUser,
  getEquipmentModel,
  uppercaseFirst,
  getEquipConfig,
  sortByValue,
  checkEqSNUniq,
  isHandpiece,
  cleanCopy,
  getActiveModels,
  buildErrorMsgFromForm,
  stopProp,
  _checkEquipmentSN,
  findModelById,
  normalizeSerialNumber,
  getEqType,
  isAerasOne,
} from '../../utils';
import { validateSerialField } from '../../_shared/serial-number-input';
import { Org } from '../../_shared/interfaces/org';
import { Sidebar } from '../equipment-details/sidebar.component';
import { get, set } from 'lodash';
import { getOrg } from '../../_shared/services/manage-orgs.service';
import { Model } from '../../_shared/interfaces/model';
import { Opt } from '../../_shared/interfaces/opt';
import { sendDeviceCommand } from '../../_shared/services/manage-schedule.service';
import { WithForm } from '../../_shared/form';
import { trackEvent, } from '../../_shared/services/analytics.service';
import eventTypes from '../../_shared/interfaces/api/eventTypes';
import { updateInstallDate, updateParticleName } from '../manufacture-process/manufacture-process.utils';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateMyEquipment: () => void;
  form?: any;
}

export interface IFormItem {
  title: () => string | string;
  var: string;
  type?: string;
  opts?: Opt[] | (() => Opt[]);
  match?: string;
  // tslint:disable-next-line:no-any
  options?: { rules: any[] };
  transform?: (str: string) => number | string;
  canDisplay?: () => boolean;
  props?: {};
  label?: string;
  onChange: () => void;
  allowClear: boolean;
  disabled?: boolean;
  subText?: (item: IFormItem) => string | ReactNode;
  style?: () => string | string;
  initialDate?: Moment;
  // type: 'serialNumber' (AER-1795)
  validateSerial?: (serial: string) => string[];
}

interface IState {
  loading: boolean;
  equipment: Equipment;
  location?: Location;
  serviceOrg?: Org;
  dentalOrg?: Org;
  toDisplay: IFormItem[];
  isEdit: boolean;
  booby?: boolean;
  cancelDate?: string;
  allEquipment?: Equipment[];
}

export class _EditEquipmentComponent extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const { history, sState } = props;

    const equipment = get(history, 'location.state.equipment');
    const model = getEquipmentModel(equipment, sState);
    const eqType = getEqType(equipment);
    const location = get(history, 'location.state.location');
    const serviceOrg = get(history, 'location.state.serviceOrg');
    const dentalOrg = get(history, 'location.state.dentalOrg');
    const isCompressor = get(model, 'type') === 'compressor';
    const toDisplay = ([
      {
        title: 'Equipment Name',
        var: 'name',
        options: {
          validateTrigger: 'onBlur',
          rules: [{ max: 25, message: 'Name is limited to 25 characters.' }],
        },
      },
      {
        title: () => {
          const {
            form: { getFieldValue },
          } = this.props;
          const model = getEquipmentModel(
            this.getEquipment(),
            this.props.sState
          );
          const val = getFieldValue('alexaNumber');
          if (model && val) {
            return `Alexa Name: ${uppercaseFirst(model.type || '')} ${val}`;
          }
          return `Alexa Name`;
        },
        type: 'number',
        var: 'alexaNumber',
        props: {
          min: 0,
          max: 99999,
          type: 'number',
        },
        canDisplay: () =>
          !isHandpiece(equipment) &&
          (userHasRole([0, 1], this.props.sState) ||
            isDentalOrgUser(this.props.sState)),
        transform: (val: string) => parseInt(val),
      },
      {
        var: 'onConsignment',
        type: 'checkbox',
        label: 'Consignment',
        canDisplay: () => userHasRole([0, 1], this.props.sState),
        options: {
          valuePropName: 'checked',
        },
      },
      {
        title: 'Service Org Purchase Order',
        var: 'soPurchaseOrder',
        canDisplay: () => true,
      },
      {
        title: 'Dental Org Purchase Order',
        var: 'doPurchaseOrder',
        canDisplay: () => true,
      },
      {
        title: 'Install Date',
        var: 'installDate',
        type: 'datepicker',
        canDisplay: () =>
          userHasRole([0, 1], this.props.sState) ||
          isServiceOrgUser(this.props.sState),
        props: {
          onBlur: async () => {
            const {
              form: { setFields, getFieldValue },
            } = this.props;
            let value = getFieldValue('installDate');
            let installDate = get(equipment, 'installDate');
            if (value) {
              setFields({
                installDate: {
                  value,
                  errors: undefined,
                },
              });
            }
            if (!installDate && value) {
              setFields({
                onConsignment: {
                  value: false,
                  errors: undefined,
                },
              });
            }
          },
        },
      },
      {
        title: 'Model Number',
        var: 'modelId',
        type: 'dropdown',
        canDisplay: () => userHasRole([0, 1], this.props.sState),
        opts: () => {
          return this.getSelectableModels().map((m: Model) => ({
            label: `${m.name} (${m.id})`,
            value: m.id,
          })).sort(sortByValue)
        },
        props: {
          // AER-1795: the SN is validated against the selected model, so re-check
          // an entered SN when the model changes.
          onChange: () => this.revalidateSerial(),
        },
      },
      {
        title: 'Equipment Serial Number',
        var: 'equipmentSN',
        // AER-1795: shared <SerialNumberInput>, validated with the same per-type
        // rules as Manufacturing Process except the build-year check. Reverses
        // AER-1597, whose false positives came from the stale hardcoded table.
        type: 'serialNumber',
        canDisplay: () => userHasRole([0, 1], this.props.sState),
        validateSerial: (serial: string) => this.validateSerial(serial),
        options: {
          validateTrigger: 'onBlur',
        },
      },
      {
        title: 'Control Module SN',
        var: 'controlModuleSN',
        canDisplay: () =>
          userHasRole([0, 1], this.props.sState) && isHandpiece(equipment),
      },
      {
        title: 'Device ID',
        var: 'deviceId',
        canDisplay: () => userHasRole([0], this.props.sState),
      },
      {
        title: isCompressor ? 'Motor Head 1 SN' : 'Motor SN',
        var: 'mSerial1',
        canDisplay: () => userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
      {
        title: 'Motor Head 2 SN',
        var: 'mSerial2',
        canDisplay: () =>
          isCompressor &&
          get(this.getEquipment(), 'mSerial2') &&
          userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
      {
        title: 'Motor Head 3 SN',
        var: 'mSerial3',
        canDisplay: () =>
          isCompressor &&
          get(this.getEquipment(), 'mSerial3') &&
          userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
      {
        title: 'Tank SN',
        var: 'tankSerial',
        canDisplay: () =>
          isCompressor &&
          get(this.getEquipment(), 'tankSerial') &&
          userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
      {
        title: 'Dryer SN',
        var: 'dryerSerial',
        canDisplay: () =>
          isCompressor &&
          get(this.getEquipment(), 'dryerSerial') &&
          userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
      {
        title: 'Vacuum Pump SN',
        var: 'pumpSerial',
        canDisplay: () =>
          !isCompressor &&
          !isHandpiece(equipment) &&
          userHasRole([0, 1], this.props.sState) && !isAerasOne(eqType),
      },
    ] as unknown) as IFormItem[];

    this.state = {
      isEdit: true,
      canEditRole: false,
      loading: false,
      equipment,
      serviceOrg,
      dentalOrg,
      location,
      toDisplay,
      cancelDate: undefined,
      allEquipment: [],
    } as IState;
  }
  trackEvent = (event = '', data = {}) => {
    trackEvent(event, { ...data, equipment: this.state.equipment });
  };

  // AER-1795: Edit Equipment applies Manufacturing's per-type SN rules for the selected
  // model, minus the build-year window, so older units can be corrected. The 12-character
  // cap (AER-1597) and uniqueness still apply.
  validateSerial = (serial: string): string[] => {
    const { form, sState } = this.props;
    const { equipment, allEquipment } = this.state;
    const modelId = form.getFieldValue('modelId') || get(equipment, 'modelId');

    // Only a changed serial/model pairing is judged. An untouched record was accepted when
    // it was built or last edited; re-judging it would block unrelated edits on legacy
    // serials, which is the AER-1597 failure mode.
    if (
      serial === normalizeSerialNumber(get(equipment, 'equipmentSN')) &&
      modelId === get(equipment, 'modelId')
    ) {
      return [];
    }

    const models = get(sState, 'dash.models', []) as Model[];
    // Route by the equipment's own type: the selected model's `type`.
    const eqType = get(findModelById(models, modelId), 'type', getEqType(equipment));
    const errors = _checkEquipmentSN(serial, modelId, eqType, {
      models,
      selectableModels: this.getSelectableModels(),
      checkBuildYear: false,
      maxLength: 12,
    }).map(e => e.message);

    if (!checkEqSNUniq(allEquipment as Equipment[], serial, equipment)) {
      const other = (allEquipment || []).find(e => e.equipmentSN === serial);
      errors.push(
        other
          ? `Validation error: Serial number ${serial} is already used by device ${other.deviceId}${other.modelId ? ` (model ${other.modelId})` : ''}.`
          : `Validation error: Serial number ${serial} is already used by another unit.`
      );
    }
    return errors;
  };

  // What the Model Number dropdown offers: active models; an Aeras One unit can only move to
  // a model of its own type, anything else to any non-Aeras One model.
  getSelectableModels = (): Model[] => {
    const { equipment } = this.state;
    const model = getEquipmentModel(equipment, this.props.sState);
    const eqType = getEqType(equipment);
    const activeModels = getActiveModels();

    if (model && isAerasOne(eqType)) {
      return activeModels.filter((m: Model) => m.type == eqType);
    }
    return activeModels.filter((m: Model) => !m.type.includes('aerasone'));
  };

  revalidateSerial = () => {
    const { form } = this.props;
    if (form.getFieldValue('equipmentSN')) {
      validateSerialField(form, 'equipmentSN');
    }
  };
  componentDidMount = () => {
    // AER-1591: SPA does not reset scroll on route change, so tapping
    // Edit Details from a scrolled Equipment Details landed this screen
    // at the prior scroll Y. Reset both the inner ContentContainer (the
    // mobile scroll viewport) and window scroll on entry.
    const contentEl = document.getElementById('ContentContainer');
    if (contentEl) contentEl.scrollTop = 0;
    if (typeof window !== 'undefined') window.scrollTo(0, 0);

    this.retrieveEquipment();
    this.setFieldsOriginal();
  };

  getEquipment = (): any => {
    return get(this, 'state.equipment');
  };
  retrieveEquipment = async () => {
    this.setState({ loading: true });

    const e = this.getEquipment();
    const allEquipment = get(this, 'props.sState.dash.equipment', []);
    try {
      const equipment = await getEquipment(e.deviceId);
      if (!equipment.installDate) {
        this.props.form.setFieldsValue({ installDate: null });
      }
      const serviceOrg = await getOrg(e.serviceOrgId).catch(() => undefined);
      this.setState({
        loading: false,
        serviceOrg,
        equipment: new _Equipment(equipment),
        allEquipment,
      });
    } catch (err) {
      this.setState({
        loading: false,
      });
    }
  };
  getItems = (item: 'locations' | 'orgs') => {
    const {
      sState: { dash },
    } = this.props;
    const items = (dash[item] as unknown) as Location[];
    return items.map(item => ({ label: item.name, value: item.id })) as Opt[];
  };

  submit = async (e?: MouseEvent | FormEvent) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    const state = { ...this.state };

    state.loading = true;
    this.setState(state);

    this.props.form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        message.error(buildErrorMsgFromForm(err));
        return this.setState({
          loading: false,
        });
      }
      try {
        const { sState } = this.props;
        const equipment = new _Equipment(this.getEquipment());
        const userId = get(sState, 'auth.user.userId');

        const originalEquipment = cleanCopy(equipment);

        this.state.toDisplay.map(item => {
          const val = item.transform
            ? item.transform(values[item.var])
            : values[item.var];

          const canDisplay = item.canDisplay ? item.canDisplay() : true;

          if (item.type === 'datepicker' && canDisplay) {
            if (val) {
              equipment[item.var] = moment(val)
                .toDate()
                .toISOString();
            } else {
              delete equipment[item.var];
            }
          } else if (val !== undefined && canDisplay) {
            equipment[item.var] = val;
          }
        });

        const dte = new Date().toISOString();
        set(equipment, 'updatedAt', dte);
        set(equipment, 'updatedBy', userId);

        if (!equipment.createdAt) {
          set(equipment, 'createdAt', dte);
        }
        if (!equipment.createdBy) {
          set(equipment, 'createdBy', userId);
        }

        if (!equipment.installDate) {
          delete (equipment as any).installDate;
        } else if (equipment.installDate && originalEquipment.installDate != equipment.installDate) {
          await updateInstallDate({ deviceId: equipment.deviceId, equipment });
        }

        const equipments = (this.state.allEquipment || []).filter(
          e => e.dentalOrgId === equipment.dentalOrgId
        );
        const model = getEquipmentModel(equipment, sState);

        const eqNums = equipments.filter(e => {
          const em = getEquipmentModel(e, sState);
          if (
            em &&
            model &&
            em.type === model.type &&
            equipment.alexaNumber &&
            e.alexaNumber === equipment.alexaNumber &&
            e.id !== equipment.id
          ) {
            return true;
          } else {
            return false;
          }
        });

        if (eqNums.length > 0) {
          message.error('That Alexa Number already exists within the Dental Orgs equipment.');
          return this.setState({
            loading: false,
          });
        }

        const updateSerial = async (
          deviceId: string,
          name: string,
          serial: string
        ) => {
          await sendDeviceCommand(
            deviceId,
            { arg: `set_sn,${name},${serial}` },
            { showErrorToast: true },
            isHandpiece(equipment)
          );
        };

        const origEquip = this.getEquipment();

        const checks = [
          { var: 'mSerial1', name: 'motor1' },
          { var: 'mSerial2', name: 'motor2' },
          { var: 'mSerial3', name: 'motor3' },
        ];

        await Promise.all(
          checks.map(async c => {
            const val = get(equipment, c.var);
            if (val && val !== get(origEquip, c.var)) {
              await updateSerial(equipment.deviceId, c.name, val as string);
            }
          })
        );

        if (get(equipment, 'modelId') !== get(origEquip, 'modelId')) {
          await sendDeviceCommand(
            equipment.deviceId,
            { arg: `set_model,${equipment.modelId}` },
            { showErrorToast: true },
            isHandpiece(equipment)
          );
        }

        // AER-1597: when the equipmentSN is changed via this form, push the
        // new SN as the Particle device name (mirrors the manufacture flow).
        if (get(equipment, 'equipmentSN') !== get(origEquip, 'equipmentSN')) {
          await updateParticleName({
            deviceId: equipment.deviceId,
            equipment,
          }).catch(() => 0);
        }

        await createOrUpdateEquipment(equipment);

        this.trackEvent(eventTypes.eq_update, {
          old: originalEquipment,
          new: equipment,
        });
        message.success('Data saved successfully!');
        this.setState({
          loading: false,
          equipment,
        });

        this.goBack();
      } catch (err) {
        message.error('Save failed: ' + err.message);
        return this.setState({
          loading: false,
        });
      }
    });
  };

  setFieldsOriginal = () => {
    const { toDisplay, equipment } = this.state;

    const {
      form: { setFieldsValue },
    } = this.props;

    toDisplay.map(item => {
      let val = equipment[item.var];
      if (item.match) {
        val = get(this, item.match, val) as any;
      }
      if (item.type === 'datepicker') {
        val = (moment(val as string) as unknown) as number;
      }
      setFieldsValue({ [item.var]: val });
    });
  };
  goBack = () => {
    this.props.history.goBack();
  };
  goToBillingHistory = (equtipmentId: string) => { };
  render() {
    const { sState } = this.props;
    const {
      toDisplay,
      loading,
      equipment,
    } = this.state;
    const { Form } = this.props.form;
    const { isPhoneBreak } = get(sState, 'dash.view') as any;
    const config = getEquipConfig(equipment, sState);
    return (
      <div css={css(styles.container)}>
        <div css={css(SharedStyles.row, `height: 100%; width: 100%;`)}>
          {!isPhoneBreak && (
            <Sidebar
              equipment={equipment}
              model={findModel(equipment, sState)}
              updateEquipment={this.retrieveEquipment}
              // @ts-ignore: getEquipConfig returns a loosely-typed value
              config={config}
              goToBillingHistory={this.goToBillingHistory}
            />
          )}

          <div css={css(styles.mainContent)}>
            <div
              css={css(
                SharedStyles.formContainer,
                `width: 80%; padding-bottom: 20px;`
              )}
            >
              <Link css={css('margin-bottom: 5px;')} onClick={this.goBack}>
                {'< Back'}
              </Link>

              <FormTitle size={'20px'} title={`Edit Equipment Information`} />

              <Form
                layout="vertical"
                onSubmit={this.submit}
                items={toDisplay}
              >
                <Button
                  title={'Cancel'}
                  disabled={loading}
                  css={css`
                    margin-right: 10px;
                  `}
                  outline={true}
                  onClick={this.goBack}
                />
                <Button
                  title={'Save'}
                  loading={loading}
                  disabled={loading}
                  onClick={this.submit}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const EditEquipmentComponent = WithForm(withRouter(_EditEquipmentComponent));
